import { ThumbnailType } from '@/schemas/thumbnail-schema.ts';
import { ExperienceIconSize } from '@components/data/experience-icon/experience-icon-constants.ts';
import { ExperienceBannerSize } from '@components/data/experience-banner/experience-banner-constants.ts';
import ClassString from '@utils/class-string.ts';
import themeStore from '@store/theme-store.ts';
import ThemeStore from '@store/theme-store.ts';
import BrandColors from '@styles/_brand_colors.ts';
import { findNearestDataPoint } from '@utils/chart-data.ts';
import modalStore from '@store/modal-store.ts';
import { AnnotationsOptions } from 'highcharts';
import thumbnailStore from '@store/thumbnail-store.ts';
import { Experience, FullExperience } from '@/schemas/experience-schema.ts';
import robuxPriceHistoryStore from '@store/robux-price-history-store.ts';
import annotationStateStore from '@store/annotation-state-store.ts';

interface ExperienceStatsAnnotationsProps {
	experience: Experience;
	data: number[][];
	options?: {
		yMinInside?: boolean;
	};
}

const ExperienceStatsAnnotations = (props: ExperienceStatsAnnotationsProps) => {
	const fullExperience = props.experience as FullExperience;
	const dailyPickDates =
		!!fullExperience.daily_pick_dates && annotationStateStore.showDailyPicks
			? fullExperience.daily_pick_dates
			: [];

	const thumbnails = thumbnailStore.getAllThumbnails(props.experience)
		? thumbnailStore
				.getAllThumbnails(props.experience)
				.filter((thumbnail) => {
					if (
						annotationStateStore.showIcons &&
						annotationStateStore.showBanners
					) {
						return true;
					}

					if (
						!annotationStateStore.showIcons &&
						thumbnail.type === ThumbnailType.ICON
					) {
						return false;
					}

					if (
						!annotationStateStore.showBanners &&
						thumbnail.type === ThumbnailType.BANNER
					) {
						return false;
					}

					return true;
				})
		: [];

	const robuxPriceHistory = annotationStateStore.showRobuxPrice
		? robuxPriceHistoryStore.experienceRobuxPriceHistory(
				props.experience.place_id
			)
		: [];

	const annotations: AnnotationsOptions[] = [
		{
			draggable: '',
			labels: [
				...thumbnails.map((thumbnail) => {
					const cutoffDate = new Date(`2024-10-03`);
					const thumbnailDate = new Date(thumbnail.time);

					const hashPrefix =
						thumbnailDate >= cutoffDate ? '180DAY-' : '';

					const imgUrl = `https://tr.rbxcdn.com/${hashPrefix}${thumbnail.hash}/${thumbnail.type === ThumbnailType.ICON ? ExperienceIconSize.SMALL : ExperienceBannerSize.SMALL}/Image/Webp/noFilter`;

					return {
						useHTML: true,
						allowOverlap: true,
						point: {
							xAxis: 0,
							yAxis: 400,
							x: new Date(thumbnail.time).getTime(),
							y: 0,
						},
						y:
							thumbnail.type === ThumbnailType.ICON ||
							!annotationStateStore.showIcons
								? -16
								: 60,
						backgroundColor: 'transparent',
						borderWidth: 0,
						text: `<div id="${hashPrefix}${thumbnail.hash}-${thumbnail.type}" class="group w-fit flex flex-col justify-center items-center gap-2 dark:text-white bg-white dark:bg-gray-525 rounded-xs outline-1 outline-gray-300 dark:outline-gray-500 cursor-pointer hover:absolute hover:z-in-front">
										<div class="${ClassString({
											static: 'flex group-touch-hover:hidden flex-col justify-center items-center gap-2 pointer-events-none',
											dynamic: {
												'min-h-8 min-w-8':
													thumbnail.type ===
													ThumbnailType.ICON,
												'min-h-6 min-w-9':
													thumbnail.type ===
													ThumbnailType.BANNER,
											},
										})}">
							                  <img src="${imgUrl}" alt="" class="${ClassString({
													static: 'pointer-events-none',
													dynamic: {
														'h-8 w-8':
															thumbnail.type ===
															ThumbnailType.ICON,
														'h-6 w-9':
															thumbnail.type ===
															ThumbnailType.BANNER,
													},
												})}">
										</div>
										
										<div class="hidden group-touch-hover:flex flex-col justify-center items-center gap-2 pointer-events-none">
							                <img src="${imgUrl}" alt="" class="${ClassString({
												static: 'pointer-events-none',
												dynamic: {
													'h-16 w-16':
														thumbnail.type ===
														ThumbnailType.ICON,
													'h-11 w-16':
														thumbnail.type ===
														ThumbnailType.BANNER,
												},
											})}">
							                <div>
							                	${thumbnail.type === ThumbnailType.ICON ? 'Icon' : 'Thumbnail'}
											</div>
											<div>
							                	${new Date(thumbnail.time).toLocaleDateString()}
							                </div>
							                <div>
							                	${new Date(thumbnail.time).toLocaleTimeString()}
							                </div>
										</div>
					               </div>`,
					};
				}),
				...dailyPickDates.map((date) => {
					return {
						useHTML: true,
						allowOverlap: true,
						point: {
							xAxis: 0,
							yAxis: props.options?.yMinInside ? 200 : 0,
							x: date.getTime(),
							y: props.options?.yMinInside ? 200 : 0,
						},
						y: props.options?.yMinInside ? 200 : 0,
						backgroundColor: 'transparent',
						borderWidth: 0,
						text: `<div id="daily-pick-${date.getTime()}" class="relative">
										<div class="group flex flex-col justify-center items-center gap-2 dark:text-white bg-white dark:bg-gray-525 rounded-xs  outline-dashed outline-1 outline-gray-600 dark:outline-white h-10 w-10 absolute top-0 left-1/2 transform -translate-x-1/2 translate-y-2">
											<img src="${themeStore.lightTheme ? '/daily-picks-black.svg' : '/daily-picks-white.svg'}" alt="" />
											<div class="${ClassString({
												static: 'absolute hidden group-touch-hover:flex justify-center items-center top-[150%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 py-1 px-2 text-sm dark:text-white bg-white dark:bg-gray-525 rounded-xs outline-1 outline-gray-600 dark:outline-white',
												dynamic: {
													'-top-[50%]':
														props.options
															?.yMinInside,
												},
											})}">
												Daily Pick
											</div>
										</div>
									</div>`,
					};
				}),
				...robuxPriceHistory.map((history) => {
					return {
						useHTML: true,
						allowOverlap: true,
						point: {
							xAxis: 0,
							yAxis: 400,
							x: history.time.getTime(),
							y: 0,
						},
						backgroundColor: 'transparent',
						borderWidth: 0,
						text: `<div id="robux-price-history-${history.time.getTime()}" class="${ClassString({ static: 'group flex flex-col justify-center items-center gap-2 dark:text-white bg-white dark:bg-gray-525 rounded-xs outline-1 outline-gray-600 dark:outline-white h-8 w-12 relative group z-in-front', dynamic: { 'w-14': history.price > 99 } })}">
										<div class="flex justify-center items-center gap-1 text-sm">
											${history.price}
											<img src="${themeStore.lightTheme ? '/robux-black.svg' : '/robux-white.svg'}" alt="" class="h-5 w-5" />
										</div>
										<div class="absolute hidden group-touch-hover:flex justify-center items-center gap-1 top-[175%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 py-1 px-4 text-xs dark:text-white bg-white dark:bg-gray-525 rounded-xs outline-1 outline-gray-600 dark:outline-white">
											Price Change:
											${history.change > 0 ? `+${history.change}` : history.change}
											<img src="${themeStore.lightTheme ? '/robux-black.svg' : '/robux-white.svg'}" alt="" class="h-5 w-5" />
										</div>
									</div>`,
					};
				}),
				...(fullExperience.released.getFullYear() > 1
					? [
							{
								useHTML: true,
								allowOverlap: true,
								point: {
									xAxis: 0,
									yAxis: props.options?.yMinInside ? 200 : 0,
									x: fullExperience.released.getTime(),
									y: props.options?.yMinInside ? 200 : 0,
								},
								y: props.options?.yMinInside ? 200 : 0,
								backgroundColor: 'transparent',
								borderWidth: 0,
								text: `<div id="release-date-${fullExperience.released.getTime()}" class="relative">
										<div class="group flex flex-col justify-center items-center gap-2 dark:text-white bg-white dark:bg-gray-525 rounded-xs  outline-dashed outline-1 outline-gray-600 dark:outline-white absolute top-0 left-1/2 transform -translate-x-1/2 translate-y-2 px-2 py-1 text-sm">
											Release
										</div>
								</div>`,
							},
						]
					: []),
			],
			shapes: [
				...thumbnails.map((thumbnail) => {
					return {
						type: 'path',
						points: [
							{
								xAxis: 0,
								yAxis: 400,
								x: new Date(thumbnail.time).getTime(),
								y: 0,
							},
							{
								xAxis: 0,
								yAxis: 0,
								x: new Date(thumbnail.time).getTime(),
								y: 0,
							},
						],
						strokeWidth: ThemeStore.lightTheme ? 0.6 : 0.75, // Line thickness
						stroke: ThemeStore.lightTheme
							? BrandColors.gray600
							: BrandColors.white, // Line color (semi-transparent black)
					};
				}),
				...dailyPickDates.map((date) => {
					const nearestDataPoint = findNearestDataPoint(
						props.data,
						date.getTime()
					);

					return {
						type: 'path',
						dashStyle: 'Dash',
						points: [
							{
								xAxis: 0,
								yAxis: 0,
								x: date.getTime(),
								y: nearestDataPoint.value,
							},
							{
								xAxis: 0,
								yAxis: 0,
								x: date.getTime(),
								y: 0,
							},
						],
						strokeWidth: 1, // Line thickness
						stroke: ThemeStore.lightTheme
							? BrandColors.gray600
							: BrandColors.white, // Line color (semi-transparent black)
					};
				}),
				...robuxPriceHistory.map((history) => {
					return {
						type: 'path',
						points: [
							{
								xAxis: 0,
								yAxis: 400,
								x: history.time.getTime(),
								y: 0,
							},
							{
								xAxis: 0,
								yAxis: 0,
								x: history.time.getTime(),
								y: 0,
							},
						],
						strokeWidth: 1, // Line thickness
						stroke: ThemeStore.lightTheme
							? BrandColors.gray600
							: BrandColors.white, // Line color (semi-transparent black)
					};
				}),
				...(fullExperience.released.getFullYear() > 1
					? [fullExperience.released].map((date) => {
							const nearestDataPoint = findNearestDataPoint(
								props.data,
								date.getTime()
							);

							return {
								type: 'path',
								dashStyle: 'Dash',
								points: [
									{
										xAxis: 0,
										yAxis: 0,
										x: date.getTime(),
										y: nearestDataPoint.value,
									},
									{
										xAxis: 0,
										yAxis: 0,
										x: date.getTime(),
										y: 0,
									},
								],
								strokeWidth: 1, // Line thickness
								stroke: ThemeStore.lightTheme
									? BrandColors.gray600
									: BrandColors.white, // Line color (semi-transparent black)
							};
						})
					: []),
			],
			events: {
				click: (clickEvent) => {
					if (clickEvent) {
						const label = clickEvent.target as HTMLDivElement;
						if (
							!label.id.includes('Icon') &&
							!label.id.includes('Banner')
						) {
							return;
						}
						const splitId = label.id.split('-');
						const prefix =
							splitId.length < 3
								? ''
								: `${label.id.split('-')[0]}-`;
						const hash =
							splitId.length < 3 ? splitId[0] : splitId[1];
						const type =
							splitId.length < 3 ? splitId[1] : splitId[2];
						const typeEnum =
							type === 'Icon'
								? ThumbnailType.ICON
								: ThumbnailType.BANNER;
						const imgUrl = `https://tr.rbxcdn.com/${prefix}${hash}/${typeEnum === ThumbnailType.ICON ? ExperienceIconSize.VERY_LARGE : ExperienceBannerSize.VERY_LARGE}/Image/Webp/noFilter`;

						modalStore.open(<img src={imgUrl} alt={''} />);
					}
				},
			},
		},
	];

	return annotations;
};

export default ExperienceStatsAnnotations;
